import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Grid, Typography, Box, Card, CardContent, Switch, FormControlLabel,
    Button, IconButton, TextField, Divider, Alert, Skeleton,
    Accordion, AccordionSummary, AccordionDetails, Select, MenuItem,
    FormControl, InputLabel, Chip, Tooltip, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import {
    ExpandMore, ArrowBack, Save, Delete, Add, Close
} from '@mui/icons-material';
import {
    fetchFmDataProduct, saveFmDataProduct, deleteFmPermissionRule
} from '../../lib/services/fmDataService';

const FinalMileAdminProductPage = ({ idToken, drawerWidth }) => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const isNew = productId === 'new';

    const emptyProduct = {
        id: '',
        name: '',
        userFriendlyAliasName: '',
        description: '',
        url: '',
        method: 'GET',
        acceptHeaderValue: 'text/csv',
        isEnabled: false,
        enablePermissionRules: false,
        isFromServiceBus: false,
        convertQueryParametersToJsonPayload: false,
        dataProductQueryParameters: [],
        permissionRules: [],
        isNew: true
    };

    const [product, setProduct] = useState(isNew ? emptyProduct : null);
    const [loading, setLoading] = useState(!isNew);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null);

    useEffect(() => {
        if (isNew || !idToken) return;
        setLoading(true);
        fetchFmDataProduct(idToken, productId)
            .then((response) => {
                if (!response.isError) {
                    setProduct(response.data);
                } else {
                    setError(response.data || 'Failed to load data product');
                }
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [idToken, productId, isNew]);

    const handleFieldChange = (field, value) => {
        setProduct(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveMessage(null);
        try {
            const response = await saveFmDataProduct(idToken, product);
            if (!response.isError) {
                setSaveMessage({ severity: 'success', text: 'Data product saved successfully.' });
                if (isNew && response.data?.id) {
                    navigate(`/finalmile-admin/${response.data.id}`, { replace: true });
                }
            } else {
                setSaveMessage({ severity: 'error', text: response.data || 'Save failed.' });
            }
        } catch (e) {
            setSaveMessage({ severity: 'error', text: e.message || 'Save failed.' });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteRule = async (ruleId) => {
        try {
            await deleteFmPermissionRule(idToken, ruleId);
            setProduct(prev => ({
                ...prev,
                permissionRules: prev.permissionRules.filter(r => r.id !== ruleId)
            }));
        } catch (e) {
            setSaveMessage({ severity: 'error', text: 'Failed to delete permission rule.' });
        }
    };

    // Query parameter management
    const addQueryParam = () => {
        setProduct(prev => ({
            ...prev,
            dataProductQueryParameters: [
                ...prev.dataProductQueryParameters,
                { id: '', name: '', parameterType: 'String', format: '', description: '', suggestedValue: '' }
            ]
        }));
    };

    const updateQueryParam = (index, field, value) => {
        setProduct(prev => {
            const params = [...prev.dataProductQueryParameters];
            params[index] = { ...params[index], [field]: value };
            return { ...prev, dataProductQueryParameters: params };
        });
    };

    const removeQueryParam = (index) => {
        setProduct(prev => ({
            ...prev,
            dataProductQueryParameters: prev.dataProductQueryParameters.filter((_, i) => i !== index)
        }));
    };

    // Permission rule management
    const addPermissionRule = () => {
        setProduct(prev => ({
            ...prev,
            permissionRules: [
                ...prev.permissionRules,
                { id: '', description: '', showAll: false, groups: [], displayFields: [], rowFilters: [] }
            ]
        }));
    };

    const updatePermissionRule = (index, field, value) => {
        setProduct(prev => {
            const rules = [...prev.permissionRules];
            rules[index] = { ...rules[index], [field]: value };
            return { ...prev, permissionRules: rules };
        });
    };

    const removePermissionRule = (index) => {
        const rule = product.permissionRules[index];
        if (rule.id) {
            handleDeleteRule(rule.id);
        } else {
            setProduct(prev => ({
                ...prev,
                permissionRules: prev.permissionRules.filter((_, i) => i !== index)
            }));
        }
    };

    // Row filter management
    const addRowFilter = (ruleIndex) => {
        setProduct(prev => {
            const rules = [...prev.permissionRules];
            rules[ruleIndex] = {
                ...rules[ruleIndex],
                rowFilters: [
                    ...(rules[ruleIndex].rowFilters || []),
                    { id: '', field: '', operator: 0, values: [] }
                ]
            };
            return { ...prev, permissionRules: rules };
        });
    };

    const updateRowFilter = (ruleIndex, filterIndex, field, value) => {
        setProduct(prev => {
            const rules = [...prev.permissionRules];
            const filters = [...(rules[ruleIndex].rowFilters || [])];
            filters[filterIndex] = { ...filters[filterIndex], [field]: value };
            rules[ruleIndex] = { ...rules[ruleIndex], rowFilters: filters };
            return { ...prev, permissionRules: rules };
        });
    };

    const removeRowFilter = (ruleIndex, filterIndex) => {
        setProduct(prev => {
            const rules = [...prev.permissionRules];
            rules[ruleIndex] = {
                ...rules[ruleIndex],
                rowFilters: rules[ruleIndex].rowFilters.filter((_, i) => i !== filterIndex)
            };
            return { ...prev, permissionRules: rules };
        });
    };

    if (loading) {
        return (
            <Grid container spacing={2} style={{ marginLeft: drawerWidth - 15 }}>
                <Grid item xs={12}>
                    <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="rectangular" height={400} />
                </Grid>
            </Grid>
        );
    }

    if (error) {
        return (
            <Grid container spacing={2} style={{ marginLeft: drawerWidth - 15 }}>
                <Grid item xs={12}>
                    <Alert severity="error">{error}</Alert>
                </Grid>
            </Grid>
        );
    }

    if (!product) return null;

    const headerValues = ['text/json', 'text/csv', 'typescript/json', 'typescript/csv'];
    const methodValues = ['GET', 'POST'];
    const parameterTypes = ['String', 'Date', 'Boolean', 'StringArray', 'NumberArray'];
    const operatorLabels = ['Equals', 'NotEquals', 'LessThan', 'LessThanEqual', 'GreaterThan', 'GreaterThanEqual', 'Contains', 'NotContains'];

    return (
        <Grid container spacing={2} style={{ marginLeft: drawerWidth - 15 }}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={() => navigate('/finalmile-admin')} sx={{ mr: 1 }}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h5">
                            {isNew ? 'Add Data Product' : 'Edit Data Product'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Save />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </Box>
                </Box>

                {saveMessage && (
                    <Alert severity={saveMessage.severity} sx={{ mb: 2 }} onClose={() => setSaveMessage(null)}>
                        {saveMessage.text}
                    </Alert>
                )}
            </Grid>

            {/* Enable / Permission Toggles */}
            <Grid item xs={12}>
                <Card elevation={2}>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={product.isEnabled}
                                            onChange={() => handleFieldChange('isEnabled', !product.isEnabled)}
                                            color="primary"
                                        />
                                    }
                                    label="Enable data product"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={product.enablePermissionRules}
                                            onChange={() => handleFieldChange('enablePermissionRules', !product.enablePermissionRules)}
                                            color="primary"
                                        />
                                    }
                                    label="Enforce Permissioning"
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            {/* Product Labels Accordion */}
            <Grid item xs={12}>
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>Product Labels</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            {!product.isFromServiceBus && (
                                <Grid item xs={12}>
                                    <TextField
                                        label="URL"
                                        value={product.url || ''}
                                        onChange={(e) => handleFieldChange('url', e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Name"
                                    value={product.name}
                                    onChange={(e) => handleFieldChange('name', e.target.value)}
                                    fullWidth
                                    size="small"
                                    disabled={product.isFromServiceBus}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Alias"
                                    value={product.userFriendlyAliasName || ''}
                                    onChange={(e) => handleFieldChange('userFriendlyAliasName', e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Description"
                                    value={product.description || ''}
                                    onChange={(e) => handleFieldChange('description', e.target.value)}
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                            {!product.isFromServiceBus && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Method</InputLabel>
                                            <Select
                                                value={product.method || 'GET'}
                                                label="Method"
                                                onChange={(e) => handleFieldChange('method', e.target.value)}
                                            >
                                                {methodValues.map(v => (
                                                    <MenuItem key={v} value={v}>{v}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Header Value</InputLabel>
                                            <Select
                                                value={product.acceptHeaderValue || 'text/csv'}
                                                label="Header Value"
                                                onChange={(e) => handleFieldChange('acceptHeaderValue', e.target.value)}
                                            >
                                                {headerValues.map(v => (
                                                    <MenuItem key={v} value={v}>{v}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </>
                            )}
                            {!product.isFromServiceBus && product.method === 'POST' && (
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={product.convertQueryParametersToJsonPayload}
                                                onChange={() => handleFieldChange('convertQueryParametersToJsonPayload', !product.convertQueryParametersToJsonPayload)}
                                                color="primary"
                                            />
                                        }
                                        label="Convert query parameters into POST body"
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>

            {/* Query Parameters Accordion */}
            <Grid item xs={12}>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>Query Parameters ({product.dataProductQueryParameters.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {product.dataProductQueryParameters.map((qp, i) => (
                            <Card key={i} variant="outlined" sx={{ mb: 2, p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2">Parameter {i + 1}</Typography>
                                    <IconButton size="small" onClick={() => removeQueryParam(i)}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label="Name"
                                            value={qp.name}
                                            onChange={(e) => updateQueryParam(i, 'name', e.target.value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Type</InputLabel>
                                            <Select
                                                value={qp.parameterType || 'String'}
                                                label="Type"
                                                onChange={(e) => updateQueryParam(i, 'parameterType', e.target.value)}
                                            >
                                                {parameterTypes.map(t => (
                                                    <MenuItem key={t} value={t}>{t}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label="Format"
                                            value={qp.format || ''}
                                            onChange={(e) => updateQueryParam(i, 'format', e.target.value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Description"
                                            value={qp.description || ''}
                                            onChange={(e) => updateQueryParam(i, 'description', e.target.value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Suggested Value"
                                            value={qp.suggestedValue || ''}
                                            onChange={(e) => updateQueryParam(i, 'suggestedValue', e.target.value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>
                                </Grid>
                            </Card>
                        ))}
                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={addQueryParam}
                        >
                            Add Parameter
                        </Button>
                    </AccordionDetails>
                </Accordion>
            </Grid>

            {/* Permission Rules Accordion */}
            <Grid item xs={12}>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>Permission Rules ({product.permissionRules.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {product.permissionRules.map((rule, ri) => (
                            <Card key={ri} variant="outlined" sx={{ mb: 2, p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle1">Rule {ri + 1}</Typography>
                                    <IconButton size="small" onClick={() => removePermissionRule(ri)} color="error">
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            label="Description"
                                            value={rule.description || ''}
                                            onChange={(e) => updatePermissionRule(ri, 'description', e.target.value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={rule.showAll}
                                                    onChange={() => updatePermissionRule(ri, 'showAll', !rule.showAll)}
                                                    color="primary"
                                                />
                                            }
                                            label="Show All Data"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>AD Groups</Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                            {(rule.groups || []).map((g, gi) => (
                                                <Chip
                                                    key={gi}
                                                    label={g}
                                                    onDelete={() => {
                                                        updatePermissionRule(ri, 'groups',
                                                            rule.groups.filter((_, idx) => idx !== gi));
                                                    }}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Box>
                                        <TextField
                                            label="Add group (press Enter)"
                                            size="small"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && e.target.value.trim()) {
                                                    updatePermissionRule(ri, 'groups',
                                                        [...(rule.groups || []), e.target.value.trim()]);
                                                    e.target.value = '';
                                                    e.preventDefault();
                                                }
                                            }}
                                            sx={{ width: 300 }}
                                        />
                                    </Grid>
                                    {!rule.showAll && (
                                        <>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Display Fields</Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                                    {(rule.displayFields || []).map((f, fi) => (
                                                        <Chip
                                                            key={fi}
                                                            label={f}
                                                            onDelete={() => {
                                                                updatePermissionRule(ri, 'displayFields',
                                                                    rule.displayFields.filter((_, idx) => idx !== fi));
                                                            }}
                                                            size="small"
                                                        />
                                                    ))}
                                                </Box>
                                                <TextField
                                                    label="Add display field (press Enter)"
                                                    size="small"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && e.target.value.trim()) {
                                                            updatePermissionRule(ri, 'displayFields',
                                                                [...(rule.displayFields || []), e.target.value.trim()]);
                                                            e.target.value = '';
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    sx={{ width: 300 }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 1 }} />
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Row Filters</Typography>
                                                {(rule.rowFilters || []).map((rf, fi) => (
                                                    <Card key={fi} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                            <Typography variant="body2">Filter {fi + 1}</Typography>
                                                            <IconButton size="small" onClick={() => removeRowFilter(ri, fi)}>
                                                                <Close fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={12} sm={4}>
                                                                <TextField
                                                                    label="Field"
                                                                    value={rf.field || ''}
                                                                    onChange={(e) => updateRowFilter(ri, fi, 'field', e.target.value)}
                                                                    fullWidth
                                                                    size="small"
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={4}>
                                                                <FormControl fullWidth size="small">
                                                                    <InputLabel>Operator</InputLabel>
                                                                    <Select
                                                                        value={rf.operator ?? 0}
                                                                        label="Operator"
                                                                        onChange={(e) => updateRowFilter(ri, fi, 'operator', e.target.value)}
                                                                    >
                                                                        {operatorLabels.map((op, oi) => (
                                                                            <MenuItem key={oi} value={oi}>{op}</MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid item xs={12} sm={4}>
                                                                <TextField
                                                                    label="Values (comma-separated)"
                                                                    value={(rf.values || []).join(', ')}
                                                                    onChange={(e) => updateRowFilter(ri, fi, 'values',
                                                                        e.target.value.split(',').map(v => v.trim()).filter(v => v))}
                                                                    fullWidth
                                                                    size="small"
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Card>
                                                ))}
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    startIcon={<Add />}
                                                    onClick={() => addRowFilter(ri)}
                                                >
                                                    Add Row Filter
                                                </Button>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Card>
                        ))}
                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={addPermissionRule}
                        >
                            Add Permission Rule
                        </Button>
                    </AccordionDetails>
                </Accordion>
            </Grid>
        </Grid>
    );
};

export default FinalMileAdminProductPage;
